import { useCallback, useMemo } from "react";
import { useEquipment } from "api/hooks";
import { addHours, format, parse } from "date-fns";
import { useSearchParams } from "react-router-dom";

import { Equipment } from "../../api/hooks/useEquipment/type.ts";
import { convertStingToDate } from "../../components/Forms/TimeSelectionForm/components/ControlledCalendar.tsx";

function filterAndMergeItems(
  items: Equipment[],
  filters: { id: string; count: number; range: number; startDate: string; endDate: string }[],
): (Equipment & {
  count: number;
  range: number;
  startDate: string;
  endDate: string;
})[] {
  // Create a map for quick lookup of count and range by id
  const filterMap = new Map<
    number,
    { count: number; range: number; startDate: string; endDate: string }
  >();
  filters.forEach((filter) => {
    filterMap.set(parseInt(filter.id), {
      count: filter.count,
      range: filter.range,
      startDate: filter.startDate,
      endDate: filter.endDate,
    });
  });

  // Filter and merge the items based on the presence of their id in the filter map
  return items
    .filter((item) => filterMap.has(item.id))
    .map((item) => {
      const additionalProps = filterMap.get(item.id)!;
      return {
        ...item,
        count: additionalProps.count,
        range: additionalProps.range,
        startDate: additionalProps.startDate,
        endDate: additionalProps.endDate,
      };
    });
}

export default function useSelectedEquipment() {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTime = useMemo(() => {
    const selectedDate = searchParams.get("d");
    const selectedTime = searchParams.get("t");
    const selectedRange = searchParams.get("r");

    const startDate = addHours(
      parse(`${selectedDate} ${selectedTime}`, "yyyy-MM-dd HH:mm", new Date()),
      2,
    );
    const endDate = addHours(startDate, Number(selectedRange));

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  }, [searchParams]);

  const startDate = convertStingToDate(searchParams.get("d"));
  const time = searchParams.get("t");
  startDate?.setHours(Number(time?.split(":")[0]));
  startDate?.setMinutes(Number(time?.split(":")[1]));

  const bookingDate = useMemo(() => {
    if (!startDate) {
      return {
        startDate: "",
        endDate: "",
      };
    }

    const endDate = addHours(startDate, Number(searchParams.get("r")));

    return {
      startDate: format(new Date(startDate), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
      endDate: format(new Date(endDate), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
    };
  }, [searchParams, startDate]);

  const { equipment, isFetching } = useEquipment(bookingDate);

  const mappedEquipment = useMemo(() => {
    const mappedResult: Equipment[] = [];

    for (const [, equipments] of Object.entries(equipment)) {
      mappedResult.push(...equipments);
    }

    return mappedResult;
  }, [equipment]);

  const selectedEquipment = useMemo(() => {
    const archivedEquipment = localStorage.getItem("equipment");
    const eqIds = searchParams.get("e");

    if (archivedEquipment) {
      const parsedEquipment = JSON.parse(archivedEquipment) as {
        id: string;
        count: number;
        range: number;
        pricePerUnit: number;
      }[];

      const res = parsedEquipment.map((el) => ({
        ...el,
        startDate: startDate?.toISOString() || "",
        endDate: startDate ? addHours(startDate, Number(el.range)).toISOString() : "",
      }));

      return filterAndMergeItems(mappedEquipment, res);
    } else if (eqIds) {
      const parsedEqIds = eqIds.split(",");
      return filterAndMergeItems(
        mappedEquipment,
        parsedEqIds.map((id) => ({
          id,
          count: 1,
          range: Number(searchParams.get("r")),
          ...selectedTime,
        })),
      );
    }

    return [];
  }, [mappedEquipment, searchParams, selectedTime, startDate]);

  const onEquipmentChange = useCallback(
    (id: number) => {
      const filteredEquipment = selectedEquipment.filter((el) => el.id !== id);

      localStorage.setItem(
        "equipment",
        JSON.stringify(
          filteredEquipment.map((eq) => ({
            id: eq.id,
            count: eq.count,
            range: eq.range,
            pricePerUnit: eq.pricePerUnit,
          })),
        ),
      );

      const eqIds = filteredEquipment.map((eq) => eq.id).toString();

      setSearchParams((prev) => {
        if (eqIds) {
          prev.set("e", eqIds);
        } else {
          prev.delete("e");
        }

        return prev;
      });
    },
    [selectedEquipment, setSearchParams],
  );

  return {
    isFetching,
    selectedEquipment,
    onEquipmentChange,
  };
}
